import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriteriaGroup, Criterion } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ListChecks, Settings, ChevronRight, ChevronDown, Search, AlertCircle, Info } from 'lucide-react';

interface CriteriaGroupsTableProps {
  criteriaGroups: CriteriaGroup[];
  evaluationStatus: EvaluationStatus;
  onManageClick: () => void;
}

/**
 * Expandable table view of criteria groups with drill-down to criteria
 */
export function CriteriaGroupsTable({ criteriaGroups, evaluationStatus, onManageClick }: CriteriaGroupsTableProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const groupCriteriaByCategory = (criteria: Criterion[]) => {
    const grouped = new Map<string, Criterion[]>();

    criteria.forEach((criterion) => {
      const category = criterion.category || t('dashboard:criteriaGroups.noCategory');
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(criterion);
    });

    return grouped;
  };

  const getImportanceBadge = (importance: string) => {
    const variants: Record<string, 'destructive' | 'default' | 'secondary' | 'outline'> = {
      Critical: 'destructive',
      High: 'default',
      Medium: 'secondary',
      Low: 'outline',
      None: 'outline',
    };

    return (
      <Badge variant={variants[importance] || 'outline'} className="text-xs">
        {t(`common:importance.${importance.toLowerCase()}`)}
      </Badge>
    );
  };

  // Filter criteria groups based on search query
  const filteredCriteriaGroups = criteriaGroups.filter((group) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const groupName = group.name.toLowerCase();

    // Check if group name matches
    if (groupName.includes(query)) return true;

    // Check if any criteria in the group matches
    return group.criteria.some((criterion) => {
      const title = criterion.title.toLowerCase();
      const description = criterion.description?.toLowerCase() || '';
      const category = criterion.category?.toLowerCase() || '';
      const hints = criterion.helpfulHints?.toLowerCase() || '';

      return (
        title.includes(query) ||
        description.includes(query) ||
        category.includes(query) ||
        hints.includes(query)
      );
    });
  });

  const totalCriteria = criteriaGroups.reduce((sum, group) => sum + group.criteria.length, 0);
  const hasCriteria = totalCriteria > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
          {t('dashboard:criteriaGroups.title')} ({criteriaGroups.length})
        </h2>
        <Button onClick={onManageClick} size="sm">
          <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('dashboard:criteriaGroups.manageButton')}
        </Button>
      </div>

      {/* Status-based Info Banner */}
      {evaluationStatus === EvaluationStatus.Organizing && (
        <div className={`flex items-start gap-3 p-3 rounded-lg border ${
          hasCriteria
            ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
            : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900'
        }`}>
          {hasCriteria ? (
            <Info className="h-5 w-5 text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <AlertCircle className="h-5 w-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          )}
          <div className="flex flex-col">
            <span className={`text-sm font-semibold ${
              hasCriteria
                ? 'text-blue-900 dark:text-blue-100'
                : 'text-amber-900 dark:text-amber-100'
            }`}>
              {t('dashboard:criteriaGroups.organizingInfo.title')}
            </span>
            <span className={`text-xs ${
              hasCriteria
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-amber-700 dark:text-amber-300'
            }`}>
              {t('dashboard:criteriaGroups.organizingInfo.description')}
            </span>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Input
          type="text"
          placeholder={t('dashboard:criteriaGroups.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          aria-label={t('dashboard:criteriaGroups.searchPlaceholder')}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>{t('dashboard:criteriaGroups.columns.groupName')}</TableHead>
              <TableHead>{t('dashboard:criteriaGroups.columns.weight')}</TableHead>
              <TableHead>{t('dashboard:criteriaGroups.columns.criteriaCount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCriteriaGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  {searchQuery.trim() ? t('common:search.noResults') : t('dashboard:criteriaGroups.empty')}
                </TableCell>
              </TableRow>
            ) : (
              filteredCriteriaGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.id);
                const categorizedCriteria = groupCriteriaByCategory(group.criteria);

                return (
                  <>
                    {/* Criteria Group Row */}
                    <TableRow
                      key={group.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          aria-label={isExpanded ? t('common:actions.collapse') : t('common:actions.expand')}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{group.weight}%</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {group.criteria.length} {t('dashboard:criteriaGroups.criteria', { count: group.criteria.length })}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Criteria Rows */}
                    {isExpanded && (
                      <>
                        {Array.from(categorizedCriteria.entries()).map(([category, criteria]) => (
                          <>
                            {/* Category Header (if category exists) */}
                            {categorizedCriteria.size > 1 && (
                              <TableRow key={`${group.id}-category-${category}`} className="bg-muted/30">
                                <TableCell></TableCell>
                                <TableCell colSpan={3} className="text-sm font-semibold text-muted-foreground pl-8">
                                  {category}
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Criteria within category */}
                            {criteria.map((criterion) => (
                              <TableRow key={criterion.id} className="bg-muted/10">
                                <TableCell></TableCell>
                                <TableCell className="pl-12">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-sm">{criterion.title}</span>
                                    {criterion.description && (
                                      <span className="text-xs text-muted-foreground line-clamp-2">
                                        {criterion.description}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>{getImportanceBadge(criterion.importance)}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {criterion.helpfulHints && (
                                    <span className="line-clamp-1">{criterion.helpfulHints}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        ))}
                      </>
                    )}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {criteriaGroups.length > 0 && (
        <p className="text-xs text-muted-foreground text-right">
          {t('dashboard:criteriaGroups.totalCriteria', { count: totalCriteria })}
        </p>
      )}
    </div>
  );
}
